import type { TemplateItem } from 'inspection-types';

import {
  deriveQuestionCapabilities,
  deriveReorderCapability,
} from '../model/capabilities';
import type { PolicyContext } from '../model/mutationPolicy';
import { isInherited } from '../model/origin';
import {
  createOpKey,
  type PendingOp,
  type StaleReason,
  type TemplatePermissions,
} from '../model/operations';
import {
  getQuestionTypeDisplay,
  type QuestionTypeIcon,
} from '../model/questionTypeCatalog';
import type { FailedOperation } from '../state/customizationState';
import { createMenuEntries, type MenuPresentation } from './createMenuEntries';
import { createReorderViewModel } from './createReorderViewModel';
import { LABEL_KEYS, type BadgeViewModel, type Message } from './messages';
import { createFailure, createSpinner } from './operationPresentation';
import type {
  ReorderViewModel,
  RowFailureViewModel,
  RowMenuEntry,
  RowSpinnerViewModel,
} from './rowTypes';

export type ResponseTypeViewModel = {
  icon: QuestionTypeIcon;
  label: Message;
};

export type QuestionRowViewModel = {
  id: string;
  label: string;
  responseType: ResponseTypeViewModel;
  isHidden: boolean;
  badges: BadgeViewModel[];
  menuLabel: Message;
  menuEntries: RowMenuEntry[];
  reorder: ReorderViewModel;
  spinner: RowSpinnerViewModel | null;
  failure: RowFailureViewModel | null;
};

// pendingOp is the operation on this question. pendingOps are the operations on any question
// that can block it too, which only a reorder does. They stay empty while no reorder is
// possible, so an operation on one question leaves the other rows unchanged.
export type QuestionOperations = {
  pendingOp: PendingOp | undefined;
  pendingOps: PolicyContext['pendingOps'];
  failure: FailedOperation | undefined;
  staleReason: StaleReason | null;
  permissions: TemplatePermissions;
};

// A menu entry is only offered when capabilities permits the user and the operation, so
// NOT_PERMITTED is never shown. MUTATION_IN_FLIGHT only blocks a reorder.
// They stay in the map to keep it exhaustive.
const MENU_PRESENTATION: MenuPresentation = {
  labelKeys: {
    hide: LABEL_KEYS.hideQuestionMenuEntry,
    unhide: LABEL_KEYS.unhideQuestionMenuEntry,
  },
  blockedMessages: {
    SAME_TARGET_IN_FLIGHT: { key: LABEL_KEYS.blockedInFlightTooltip },
    REORDER_IN_FLIGHT: { key: LABEL_KEYS.blockedReorderInFlightTooltip },
    MUTATION_IN_FLIGHT: null,
    REPLICA_STALE: { key: LABEL_KEYS.blockedStaleTooltip },
    NOT_PERMITTED: null,
  },
};

function createBadges(item: TemplateItem, isHidden: boolean): BadgeViewModel[] {
  const lock: BadgeViewModel[] = isInherited(item)
    ? [{ kind: 'lock', tooltipKey: LABEL_KEYS.lockQuestionTooltip }]
    : [];
  const hidden: BadgeViewModel[] = isHidden
    ? [{ kind: 'hidden', tooltipKey: LABEL_KEYS.hiddenQuestionTooltip }]
    : [];

  return [...lock, ...hidden];
}

function createPolicyContext(operations: QuestionOperations): PolicyContext {
  const { pendingOp, pendingOps, staleReason, permissions } = operations;
  const ownOps = pendingOp
    ? { [createOpKey(pendingOp.kind, pendingOp.targetId)]: pendingOp }
    : {};

  return {
    pendingOps: { ...pendingOps, ...ownOps },
    staleReason,
    permissions,
  };
}

function createQuestionMenuEntries(
  item: TemplateItem,
  isSectionHidden: boolean,
  policyContext: PolicyContext
): RowMenuEntry[] {
  const capabilities = deriveQuestionCapabilities(
    { question: item, section: { removed: isSectionHidden } },
    policyContext
  );

  return createMenuEntries(capabilities, MENU_PRESENTATION);
}

function createReorder(
  item: TemplateItem,
  policyContext: PolicyContext
): ReorderViewModel {
  const handleLabel = {
    key: LABEL_KEYS.reorderQuestionHandleLabel,
    params: { question: item.label },
  };

  return createReorderViewModel(
    deriveReorderCapability(policyContext),
    handleLabel
  );
}

function createResponseType(questionType: string): ResponseTypeViewModel {
  const display = getQuestionTypeDisplay(questionType);

  if (display.kind === 'pointScale') {
    return {
      icon: display.icon,
      label: {
        key: LABEL_KEYS.pointScaleResponseType,
        params: { points: display.points },
      },
    };
  }

  return {
    icon: display.icon,
    label: {
      key: LABEL_KEYS.responseTypeName,
      params: { name: display.displayName },
    },
  };
}

export function createQuestionRowViewModel(
  item: TemplateItem,
  isSectionHidden: boolean,
  operations: QuestionOperations
): QuestionRowViewModel {
  const isHidden = Boolean(item.removed) || isSectionHidden;
  const responseType = createResponseType(item.questionType);
  const badges = createBadges(item, isHidden);
  const policyContext = createPolicyContext(operations);
  const menuEntries = createQuestionMenuEntries(
    item,
    isSectionHidden,
    policyContext
  );
  const spinner = createSpinner(operations.pendingOp, item.label);
  const failure = createFailure(operations.failure, item.label);

  return {
    id: item.id,
    label: item.label,
    responseType,
    isHidden,
    badges,
    menuLabel: {
      key: LABEL_KEYS.questionMenuButtonLabel,
      params: { question: item.label },
    },
    menuEntries,
    reorder: createReorder(item, policyContext),
    spinner,
    failure,
  };
}
